import { supabase } from '@shared/lib/supabase/client'
import type { Database } from '@shared/types/database'

type ZaakType = Database['public']['Tables']['zaak_types']['Row']

class ZaakTypesService {
  async getAll(): Promise<ZaakType[]> {
    const { data, error } = await supabase
      .from('zaak_types')
      .select('*')
      .order('naam')

    if (error) {
      console.error('Error fetching zaak types:', error)
      return []
    }

    return data || []
  }

  async getById(id: string): Promise<ZaakType | null> {
    const { data, error } = await supabase
      .from('zaak_types')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      console.error('Error fetching zaak type:', error)
      return null
    }

    return data
  }

  async getByCode(code: string): Promise<ZaakType | null> {
    const { data, error } = await supabase
      .from('zaak_types')
      .select('*')
      .eq('code', code)
      .single()

    if (error) {
      console.error('Error fetching zaak type by code:', error)
      return null
    }

    return data
  }

  async getWithCaseCount(filters?: {
    searchQuery?: string
    rechtsgebiedIds?: string[]
    instantieIds?: string[]
    dateFrom?: string
    dateTo?: string
    portalIds?: string[]
  }): Promise<(ZaakType & { case_count: number })[]> {
    // Use provided portal IDs or empty array (database will use default)
    const portalIds = filters?.portalIds || [];

    // Try search-enabled RPC function first if search query is provided
    if (filters?.searchQuery) {
      const { data, error } = await (supabase
        .rpc as any)('get_zaak_types_with_search_counts', {
          search_query: filters.searchQuery,
          filter_rechtsgebied_ids: filters.rechtsgebiedIds || null,
          filter_instantie_ids: filters.instantieIds || null,
          filter_date_from: filters.dateFrom || null,
          filter_date_to: filters.dateTo || null,
          p_portals: portalIds
        })

      if (!error) {
        return (data || []).map((item: any) => ({
          ...item,
          case_count: Number(item.case_count) || 0
        }))
      }
      console.error('Error fetching search-filtered zaak types counts:', error)
    }

    // Try filtered RPC function without search
    if (filters && (filters.rechtsgebiedIds?.length || filters.instantieIds?.length || filters.dateFrom || filters.dateTo)) {
      const { data, error } = await (supabase
        .rpc as any)('get_zaak_types_with_filtered_counts', {
          filter_rechtsgebied_ids: filters.rechtsgebiedIds || null,
          filter_instantie_ids: filters.instantieIds || null,
          filter_date_from: filters.dateFrom || null,
          filter_date_to: filters.dateTo || null,
          p_portals: portalIds
        })

      if (!error) {
        return (data || []).map((item: any) => ({
          ...item,
          case_count: Number(item.case_count) || 0
        }))
      }
      console.error('Error fetching filtered zaak types counts:', error)
    }

    // Fallback to unfiltered RPC function
    const { data, error } = await (supabase
      .rpc as any)('get_zaak_types_with_counts', {
        p_portals: portalIds
      })

    if (error) {
      console.error('Error fetching zaak types with counts:', error)
      // Fallback to regular fetch without counts
      const { data: fallbackData } = await supabase
        .from('zaak_types')
        .select('*')
        .order('naam')

      return (fallbackData || []).map(item => ({
        ...item,
        case_count: 0
      }))
    }

    return (data || []).map((item: any) => ({
      ...item,
      case_count: Number(item.case_count) || 0
    }))
  }

  async getMostUsed(limit: number = 10): Promise<ZaakType[]> {
    // Since the RPC function doesn't exist yet, use a fallback approach
    // This can be replaced with the RPC call once the database function is created
    const { data, error } = await supabase
      .from('zaak_types')
      .select(`
        *,
        jurisprudentie_zaak_types(count)
      `)
      .order('naam')
      .limit(limit)

    if (error) {
      console.error('Error fetching most used zaak types:', error)
      return []
    }

    // Sort by case count and return the most used
    const sorted = (data || [])
      .map(item => ({
        ...item,
        case_count: item.jurisprudentie_zaak_types?.[0]?.count || 0
      }))
      .sort((a, b) => b.case_count - a.case_count)
      .map(({ case_count: _case_count, ...zaakType }) => zaakType as ZaakType)

    return sorted
  }
}

export const zaakTypesService = new ZaakTypesService()